import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, MagnifyingGlass, Warning, Info, CaretDown } from '@phosphor-icons/react';

const SEVERITY_STYLES = {
  info: 'bg-zinc-700/40 text-zinc-300 border-zinc-600',
  warning: 'bg-amber-900/30 text-amber-400 border-amber-700/40',
  critical: 'bg-red-900/30 text-red-400 border-red-700/40',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: '', action: '', user_email: '' });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '200');
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.action) params.append('action', filters.action);
      if (filters.user_email) params.append('user_email', filters.user_email);
      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/audit/logs?${params.toString()}`),
        api.get('/audit/summary'),
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setSummary(summaryRes.data);
    } catch (e) {
      console.error('Failed to fetch audit logs', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">Immutable record of all platform actions</p>
        </div>
        <Badge variant="outline" className="text-xs text-zinc-400 border-zinc-700">
          {total} entries
        </Badge>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Last 24h</p>
              <p className="text-2xl font-bold mt-1" data-testid="audit-24h-count">{summary.total_24h}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Last 7 Days</p>
              <p className="text-2xl font-bold mt-1">{summary.total_7d}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Warnings</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">{summary.by_severity?.warning || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Critical</p>
              <p className="text-2xl font-bold text-red-400 mt-1">{summary.by_severity?.critical || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MagnifyingGlass size={18} className="text-blue-400" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <div className="relative w-44">
            <select
              data-testid="filter-severity"
              value={filters.severity}
              onChange={(e) => handleFilter('severity', e.target.value)}
              className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
            <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
          <Input
            data-testid="filter-action"
            placeholder="Filter by action..."
            value={filters.action}
            onChange={(e) => handleFilter('action', e.target.value)}
            className="w-52 h-9 bg-zinc-800 border-zinc-700 text-sm"
          />
          <Input
            data-testid="filter-email"
            placeholder="Filter by email..."
            value={filters.user_email}
            onChange={(e) => handleFilter('user_email', e.target.value)}
            className="w-52 h-9 bg-zinc-800 border-zinc-700 text-sm"
          />
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-zinc-500">Loading audit entries...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No audit entries found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 text-left font-medium">Timestamp</th>
                    <th className="px-4 py-3 text-left font-medium">Severity</th>
                    <th className="px-4 py-3 text-left font-medium">Action</th>
                    <th className="px-4 py-3 text-left font-medium">User</th>
                    <th className="px-4 py-3 text-left font-medium">Resource</th>
                    <th className="px-4 py-3 text-left font-medium">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={log.audit_id || i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3 text-zinc-400 whitespace-nowrap text-xs font-mono">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info}`}>
                          {log.severity === 'critical' ? <Warning size={12} /> : log.severity === 'warning' ? <Warning size={12} /> : <Info size={12} />}
                          {log.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 font-mono text-xs">{log.action}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{log.user_email || '-'}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs max-w-[200px] truncate">{log.resource_id || log.resource_type || '-'}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{log.ip || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
